import React from 'react'
import PropTypes from 'prop-types'
import { Box, TableCell, TableRow, TextField, Typography } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPortrait } from '@fortawesome/pro-solid-svg-icons/faPortrait'
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope'
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone'
import { cloneDeep, size } from 'lodash'

import telephone from '@scoir/components-core/dist/components/telephone'

import SectionTable from './components/SectionTable'

const useStyles = makeStyles((theme) => {
  return {
    sectionRow: {
      height: 55,
    },
    subLabel: {
      color: theme.palette.text.secondary,
    },
    iconCol: {
      maxWidth: '5px',
    },
    textField: {
      minWidth: '200px',
    },
  }
})

const getDefaultContactInfo = () => {
  return {
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '',
    phoneNumber: '',
  }
}

const nameIsValid = (str) => {
  return /^[a-zA-Z\s-]*$/.test(str)  
}

const phoneNumberIsValid = (str) => {
  const unformattedNum = size(telephone.unformat(str))
  return unformattedNum === 10 && /^[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/g.test(str) // eslint-disable-line no-useless-escape
}

const countryCodeIsValid = (str) => {
  return /^(\+?\d{1,3}|\d{1,4})$/.test(str)  
}

const emailIsValid = (str) => {
  return /^\w+([\.+_-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(str) // eslint-disable-line no-useless-escape
}

const cloneState = (applicationConfig) => {
  const updatedState = cloneDeep(applicationConfig)
  updatedState.admissionsContactInfo = updatedState.admissionsContactInfo ?? getDefaultContactInfo()

  return updatedState
}

const ContactSection = ({ isEditing, applicationConfig, setApplicationConfig, loading }) => {
  const contactInfo = applicationConfig.admissionsContactInfo ?? getDefaultContactInfo()
  const classes = useStyles()

  const onFirstNameChange = (newVal) => {
    const updatedState = cloneState(applicationConfig)
    updatedState.admissionsContactInfo.firstName = newVal

    onFormChange(updatedState)
  }

  const onLastNameChange = (newVal) => {
    const updatedState = cloneState(applicationConfig)
    updatedState.admissionsContactInfo.lastName = newVal

    onFormChange(updatedState)
  }

  const onEmailChange = (newVal) => {
    const updatedState = cloneState(applicationConfig)
    updatedState.admissionsContactInfo.email = newVal

    onFormChange(updatedState)
  }

  const onCountryCodeChange = (newVal) => {
    const updatedState = cloneState(applicationConfig)
    updatedState.admissionsContactInfo.countryCode = newVal

    onFormChange(updatedState)
  }

  const onPhoneNumberChange = (newVal) => {
    const updatedState = cloneState(applicationConfig)
    updatedState.admissionsContactInfo.phoneNumber = newVal

    onFormChange(updatedState)
  }

  const onFormChange = (updatedState) => {
    const errors = validateForm(updatedState)

    setApplicationConfig(updatedState, errors.length > 0)
  }

  const firstNameFieldId = 'first-name-id'
  const lastNameFieldId = 'last-name-id'
  const emailFieldId = 'email-id'
  const countryCodeFieldId = 'country-code-id'
  const phoneNumberFieldId = 'phone-number-id'

  const validateForm = (cfg) => {
    const errors = []
    const contactInfo = { ...cfg.admissionsContactInfo }

    if (
      contactInfo.firstName ||
      contactInfo.lastName ||
      contactInfo.email ||
      contactInfo.countryCode ||
      contactInfo.phoneNumber
    ) {
      if (!contactInfo.firstName || !nameIsValid(contactInfo.firstName)) {
        errors.push({ id: firstNameFieldId, msg: 'First Name is invalid' })
      }

      if (!contactInfo.lastName || !nameIsValid(contactInfo.lastName)) {
        errors.push({ id: lastNameFieldId, msg: 'Last Name is invalid' })
      }

      if (!contactInfo.email || !emailIsValid(contactInfo.email)) {
        errors.push({ id: emailFieldId, msg: 'Email is invalid' })
      }

      if (!contactInfo.countryCode || !countryCodeIsValid(contactInfo.countryCode)) {
        errors.push({ id: countryCodeFieldId, msg: 'Country Code is invalid' })
      }

      if (!contactInfo.phoneNumber || !phoneNumberIsValid(contactInfo.phoneNumber)) {
        errors.push({ id: phoneNumberFieldId, msg: 'Phone Number is invalid' })
      }
    }
    return errors
  }

  const formErrors = validateForm(applicationConfig)

  const editingForm = () => {
    return (
      <SectionTable
        sectionName="Contact Information"
        showFirstYearHeader={false}
        showTransferHeader={false}
      >
        <TableRow
          data-testid="contact-section-name-row"
          key="name"
          className={classes.sectionRow}
        >
          <TableCell
            align="left"
            variant="body"
            component="th"
            size="small"
          >
            <Box>
              <Typography variant="body1">Name</Typography>
            </Box>
          </TableCell>
          <TableCell
            align="right"
            size="small"
          >
            {loading && (
              <Skeleton
                data-testid="loading-skeleton-testid"
                width={115}
                height={18}
              />
            )}
            {!loading && (
              <TextField
                required
                value={contactInfo.firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                type="text"
                label="First Name"
                className={classes.textField}
                id={firstNameFieldId}
                error={formErrors.find((e) => e.id === firstNameFieldId) ? true : false}
                autoComplete="given-name"
                fullWidth
              />
            )}
          </TableCell>
          <TableCell
            align="right"
            size="small"
          >
            {loading && (
              <Skeleton
                data-testid="loading-skeleton-testid"
                width={115}
                height={18}
              />
            )}
            {!loading && (
              <TextField
                required
                value={contactInfo.lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                type="text"
                label="Last Name"
                className={classes.textField}
                id={lastNameFieldId}
                error={formErrors.find((e) => e.id === lastNameFieldId) ? true : false}
                autoComplete="family-name"
                fullWidth
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow
          data-testid="contact-section-email-row"
          key="email"
          className={classes.sectionRow}
        >
          <TableCell
            align="left"
            variant="body"
            component="th"
            size="small"
          >
            <Box>
              <Typography variant="body1">Email</Typography>
            </Box>
          </TableCell>
          <TableCell
            align="right"
            size="small"
            colSpan={2}
          >
            {loading && (
              <Skeleton
                data-testid="loading-skeleton-testid"
                width={115}
                height={18}
              />
            )}
            {!loading && (
              <TextField
                required
                value={contactInfo.email}
                onChange={(e) => onEmailChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                type="text"
                label="Email"
                fullWidth={true}
                className={classes.textField}
                id={emailFieldId}
                error={formErrors.find((e) => e.id === emailFieldId) ? true : false}
                autoComplete="email"
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow
          data-testid="contact-section-phone-row"
          key="phone"
          className={classes.sectionRow}
        >
          <TableCell
            align="left"
            variant="body"
            component="th"
            size="small"
          >
            <Box>
              <Typography variant="body1">Phone</Typography>
            </Box>
          </TableCell>
          <TableCell
            align="right"
            size="small"
          >
            {loading && (
              <Skeleton
                data-testid="loading-skeleton-testid"
                width={48}
                height={18}
              />
            )}
            {!loading && (
              <TextField
                required
                value={contactInfo.countryCode}
                onChange={(e) => onCountryCodeChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                type="text"
                label="Country Code"
                id={countryCodeFieldId}
                error={formErrors.find((e) => e.id === countryCodeFieldId) ? true : false}
                autoComplete="tel-country-code"
                fullWidth
              />
            )}
          </TableCell>
          <TableCell
            align="right"
            size="small"
          >
            {loading && (
              <Skeleton
                data-testid="loading-skeleton-testid"
                width={115}
                height={18}
              />
            )}
            {!loading && (
              <TextField
                required
                value={contactInfo.phoneNumber}
                onChange={(e) => onPhoneNumberChange(e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                size="small"
                type="text"
                label="Phone Number"
                className={classes.textField}
                id={phoneNumberFieldId}
                error={formErrors.find((e) => e.id === phoneNumberFieldId) ? true : false}
                autoComplete="tel-national"
                fullWidth
              />
            )}
          </TableCell>
        </TableRow>
      </SectionTable>
    )
  }

  const readOnlyForm = () => {
    return (
      <SectionTable
        sectionName="Contact Information"
        showFirstYearHeader={false}
        showTransferHeader={false}
      >
        <TableRow className={classes.sectionRow}>
          <TableCell colSpan={3}>
            <Box
              display="flex"
              alignItems="center"
              mb={2}
              data-testid="contact-section-name-read-only-row"
            >
              <Box mr={1}>
                <FontAwesomeIcon icon={faPortrait} />
              </Box>
              <Box>
                {loading && (
                  <Skeleton
                    data-testid="loading-skeleton-testid"
                    width={115}
                    height={18}
                  />
                )}
                {!loading && (
                  <Box>
                    <Typography
                      variant="body2"
                      className={classes.subLabel}
                    >
                      Name
                    </Typography>
                    <Typography
                      variant="body1"
                      id={firstNameFieldId}
                    >{`${contactInfo.firstName} ${contactInfo.lastName}`}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              data-testid="contact-section-email-read-only-row"
              mb={2}
            >
              <Box mr={1}>
                <FontAwesomeIcon icon={faEnvelope} />
              </Box>
              <Box>
                {loading && (
                  <Skeleton
                    data-testid="loading-skeleton-testid"
                    width={115}
                    height={18}
                  />
                )}
                {!loading && (
                  <Box>
                    <Typography
                      variant="body2"
                      className={classes.subLabel}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      id={firstNameFieldId}
                    >
                      {contactInfo.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              mb={2}
              alignItems="center"
              data-testid="contact-section-phone-read-only-row"
            >
              <Box mr={1}>
                <FontAwesomeIcon icon={faPhone} />
              </Box>
              <Box>
                {loading && (
                  <Skeleton
                    data-testid="loading-skeleton-testid"
                    width={115}
                    height={18}
                  />
                )}
                {!loading && (
                  <Box>
                    <Typography
                      variant="body2"
                      className={classes.subLabel}
                    >
                      Phone
                    </Typography>
                    {contactInfo.phoneNumber && (
                      <Typography
                        variant="body1"
                        id={firstNameFieldId}
                      >{`(${contactInfo.countryCode}) ${contactInfo.phoneNumber}`}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      </SectionTable>
    )
  }

  return isEditing ? editingForm() : readOnlyForm()
}

ContactSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default ContactSection
